import { Combobox } from "@/modern/components/ui/combobox";
import React, { useMemo, useState, useEffect } from "react";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { ScrollArea, ScrollBar } from "@/modern/components/ui/scroll-area";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ISwivuStore } from "@/types/SwivuStore";
import { EntityConfig, PillItemConfig } from "@/other/constants/EntityManagerConfig";
import { useDebounce } from "@/react/hooks/useDebounce";
import { Badge } from "@/modern/components/ui/badge";
import { Input } from "@/modern/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Button } from "@/modern/components/ui/button";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { getFontAwesomeClassName } from "@/other/utils/utils";
import { Model } from "@bryntum/schedulerpro";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { ColorPicker } from "@/modern/components/color-picker";
import { Separator } from "@/modern/components/ui/separator";

export function EntityPillBar({
  record,
  entityConfig,
  isDirectUpdates = true,
  wrapperElementClassName,
  contentElementClassName,
  itemElementClassName,
  noScrollArea = false,
  className,
  onFieldChange,
  isFieldDisabled = false,
  showColor = false
}: {
  record: EntityRecord;
  entityConfig: EntityConfig;
  isDirectUpdates?: boolean;
  wrapperElementClassName?: string;
  contentElementClassName?: string;
  itemElementClassName?: string;
  noScrollArea?: boolean;
  className?: string;
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: PillItemConfig) => void;
  isFieldDisabled?: boolean;
  showColor?: boolean;
}) {
  const { version: entityUpdatesVersion } = useSwivuStore<EntityRecord>({ storeId: entityConfig.storeKey });

  const pillContent = (
    <div className={cn("bg-sidebar-background/20 py-6", wrapperElementClassName)}>
      <div className={cn("flex  gap-8", contentElementClassName)}>
        {entityConfig.pillItems.map((itemConfig, index: number) => (
          <React.Fragment key={itemConfig.label || index}>
            <PillItem
              record={record}
              itemConfig={itemConfig}
              isDirectUpdates={isDirectUpdates}
              onFieldChange={onFieldChange}
              isFieldDisabled={isFieldDisabled}
              itemElementClassName={itemElementClassName}
              entityUpdatesVersion={entityUpdatesVersion}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const handleColorChange = (color: string | null) => {
    record.set('color', color);
  };

  return (
    <div className={cn("flex justify-between min-w-0 items-center px-6 gap-4", className)}>
      {showColor && (
        <div className="flex items-center gap-3 flex-shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ColorPicker
                  value={record?.color}
                  onChange={handleColorChange}
                  clearable={true}
                >
                  <button
                    type="button"
                    className="relative flex-shrink-0 size-[1rem] rounded-full overflow-hidden hover:scale-110 transition-transform cursor-pointer group border border-border/50"
                    style={{
                      backgroundColor: record?.color || ''
                    }}
                  >
                    <div className="absolute inset-0 bg-white/0 group-hover:bg-white/20 transition-colors" />
                  </button>
                </ColorPicker>
              </div>
            </TooltipTrigger>
            <TooltipContent>Click to change task color</TooltipContent>
          </Tooltip>

          <Separator orientation="vertical" className="h-4" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        {noScrollArea ? (
          pillContent
        ) : (
          <ScrollArea>
            {pillContent}
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </div>
    </div>
  );
}

const PillItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  itemElementClassName,
  entityUpdatesVersion
}: {
  record: EntityRecord;
  itemConfig: PillItemConfig;
  isDirectUpdates?: boolean;
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: PillItemConfig) => void;
  isFieldDisabled?: boolean;
  itemElementClassName?: string;
  entityUpdatesVersion: number;
}) => {

  if (itemConfig.type === "input") {
    return (
      <InputPillItem
        record={record}
        itemConfig={itemConfig}
        isDirectUpdates={isDirectUpdates}
        onFieldChange={onFieldChange}
        isFieldDisabled={isFieldDisabled}
        itemElementClassName={itemElementClassName}
        entityUpdatesVersion={entityUpdatesVersion}
      />
    );
  }

  if (itemConfig.type === "icon") {
    return (
      <IconPillItem
        record={record}
        itemConfig={itemConfig}
        isDirectUpdates={isDirectUpdates}
        onFieldChange={onFieldChange}
        isFieldDisabled={isFieldDisabled}
        itemElementClassName={itemElementClassName}
        entityUpdatesVersion={entityUpdatesVersion}
      />
    );
  }

  return (
    <ComboboxPillItem
      record={record}
      itemConfig={itemConfig}
      isDirectUpdates={isDirectUpdates}
      onFieldChange={onFieldChange}
      isFieldDisabled={isFieldDisabled}
      itemElementClassName={itemElementClassName}
      entityUpdatesVersion={entityUpdatesVersion}
    />
  );
};

const ComboboxPillItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  itemElementClassName,
  entityUpdatesVersion
}: {
  record: EntityRecord;
  itemConfig: Exclude<PillItemConfig, { type: "input" | "icon" }>;
  isDirectUpdates?: boolean;
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: PillItemConfig) => void;
  isFieldDisabled?: boolean;
  itemElementClassName?: string;
  entityUpdatesVersion: number;
}) => {
  const context = useSwivuBryntumContext();
  const { project, editorHandler, entityManager, viewManager } = context;
  const { storeKey, filterFn, fieldConfig, label, placeholder, multiSelect, showBadges, createOnUnmatched, optionItemActionRenderer, disabled } = itemConfig;
  const store = project.getCrudStore(storeKey) as unknown as ISwivuStore<EntityRecord>;
  const { version } = useSwivuStore({ storeId: storeKey });

  const isDisabled = disabled ? disabled(record, context) : false;

  // Local state for display
  const [localValue, setLocalValue] = useState<number | string | (number | string)[] | null>(record[fieldConfig.name]);

  // Sync local state when record changes
  useEffect(() => {
    setLocalValue(record[fieldConfig.name]);
  }, [record, fieldConfig.name, entityUpdatesVersion]);

  const options = useMemo(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return store
      .query((record: EntityRecord) => filterFn(record) && !record.isPhantom && !record.isGroupHeader && (canShowArchivedRecords || !record.archived))
      .map((record: EntityRecord) => ({
        label: record[fieldConfig.displayField],
        value: record[fieldConfig.valueField]
      }));
  }, [version, store, filterFn, fieldConfig]);

  const handleValueChange = (value: number | string | (number | string)[] | null) => {
    setLocalValue(value);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, value);
    }

    // Call the onFieldChange callback
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value }, record.data, itemConfig);
    }
  };

  return (
    <div className={cn("flex items-center gap-2 min-w-0 flex-shrink-0", itemElementClassName)}>
      <label className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">{label}</label>
      <Combobox
        options={options}
        value={localValue ?? undefined}
        onValueChange={handleValueChange}
        placeholder={placeholder}
        multiSelect={multiSelect}
        searchPlaceholder={`Search ${label}...`}
        maxDisplay={2}
        showBadges={showBadges}
        size="sm"
        popoverContentClassName="w-max z-[--swivu-bryntum-z-index]"
        enableRemoveButton
        disabled={isDisabled || isFieldDisabled}
        createOnUnmatched={(value) => createOnUnmatched?.({ value, record, editorHandler, project, viewManager })}
        optionItemActionRenderer={(option) => {
          const optionRecord = store.getById(option.value);
          return optionRecord ? optionItemActionRenderer?.({ record: optionRecord, editorHandler, entityManager }) : null;
        }}
      />
    </div>
  );
};

const InputPillItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  itemElementClassName,
  entityUpdatesVersion
}: {
  record: EntityRecord;
  itemConfig: Extract<PillItemConfig, { type: "input" }>;
  isDirectUpdates?: boolean;
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: PillItemConfig) => void;
  isFieldDisabled?: boolean;
  itemElementClassName?: string;
  entityUpdatesVersion: number;
}) => {
  const { fieldName, label, placeholder, inputType = "text" } = itemConfig;

  const initialValue = record[fieldName] || "";
  const [localValue, setLocalValue] = useState<string>(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const debouncedValue = useDebounce(localValue, 500);

  useEffect(() => {
    const currentValue = record[fieldName] || "";
    setLocalValue(currentValue);
  }, [record, fieldName, entityUpdatesVersion]);

  useEffect(() => {
    const currentRecordValue = record[fieldName] || "";
    if (debouncedValue !== currentRecordValue && debouncedValue !== initialValue) {
      if (isDirectUpdates) {
        record.set(fieldName, debouncedValue);
      }

      // Call the onFieldChange callback
      if (onFieldChange) {
        onFieldChange({ fieldName, value: debouncedValue }, record.data, itemConfig);
      }
    }
  }, [debouncedValue, record, fieldName, initialValue, onFieldChange, itemConfig]);

  const handleBadgeClick = () => {
    if (!isFieldDisabled) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <div className={cn("flex items-center gap-2 min-w-0 flex-shrink-0", itemElementClassName)}>
      <label className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">{label}</label>
      {isEditing ? (
        <Input
          type={inputType}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus
          disabled={isFieldDisabled}
          className="!h-auto py-[4px] px-3 w-[120px] !text-swivu-sm !leading-none rounded-full border focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        <Badge
          variant="muted"
          size="sm"
          onClick={handleBadgeClick}
          className={cn(
            "cursor-pointer hover:bg-accent transition-colors min-w-[120px] justify-start !text-swivu-sm px-3",
            !localValue && "text-muted-foreground",
            isFieldDisabled && "cursor-not-allowed opacity-50"
          )}
        >
          {localValue || placeholder || "Click to edit"}
        </Badge>
      )}
    </div>
  );
};

const IconPillItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  itemElementClassName,
  entityUpdatesVersion
}: {
  record: EntityRecord;
  itemConfig: Extract<PillItemConfig, { type: "icon" }>;
  isDirectUpdates?: boolean;
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: PillItemConfig) => void;
  isFieldDisabled?: boolean;
  itemElementClassName?: string;
  entityUpdatesVersion: number;
}) => {
  const { project } = useSwivuBryntumContext();
  const { storeKey, filterFn, fieldConfig, label } = itemConfig;
  const store = project.getCrudStore(storeKey) as unknown as ISwivuStore<EntityRecord>;
  const { version } = useSwivuStore<Model>({ storeId: storeKey, subscriberFn: ({ record: changedRecord }) => changedRecord.id === record.id });
  const [open, setOpen] = useState(false);

  // Local state for display
  const [selectedValue, setSelectedValue] = useState<number | string | null>(record[fieldConfig.name]);

  // Sync local state when record changes
  useEffect(() => {
    setSelectedValue(record[fieldConfig.name]);
  }, [record, fieldConfig.name, entityUpdatesVersion]);

  const teamColor = record._team?.color || "";

  const options = useMemo(() => {
    return store
      .query((record: EntityRecord) => filterFn(record) && !record.isPhantom && !record.isGroupHeader)
      .map((record: EntityRecord) => {
        const tagValue = record[fieldConfig.displayField];
        const iconClassName = getFontAwesomeClassName(tagValue);
        return {
          label: "", // Empty label since we're showing icon
          value: record[fieldConfig.valueField],
          icon: <i className={`fa ${iconClassName}`} style={{ color: teamColor }} />
        };
      });
  }, [version, store, filterFn, fieldConfig, teamColor]);

  // Get selected icon for display
  const selectedIconNode = useMemo(() => {
    if (selectedValue) {
      const selectedRecord = store.getById(selectedValue);
      if (selectedRecord) {
        const tagValue = selectedRecord[fieldConfig.displayField];
        const iconClassName = getFontAwesomeClassName(tagValue);
        return <i className={`fa ${iconClassName}`} style={{ color: teamColor }} />;
      }
    }
    return null;
  }, [selectedValue, fieldConfig, store, teamColor]);

  const handleValueChange = (value: number | string | (number | string)[] | null) => {
    setSelectedValue(value as number | string | null);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, value);
    }
    setOpen(false);

    // Call the onFieldChange callback
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value }, record.data, itemConfig);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(null);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, null);
    }

    // Call the onFieldChange callback
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value: null }, record.data, itemConfig);
    }
  };

  return (
    <div className={cn("flex items-center gap-2 min-w-0 flex-shrink-0", itemElementClassName)}>
      <label className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">{label}</label>
      <div className="flex items-center gap-1">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button className="relative group" type="button" variant="ghost" size="icon" onClick={() => setOpen(!open)} disabled={isFieldDisabled}>
                    {selectedValue && !isFieldDisabled && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="absolute -right-0.5 -top-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-destructive hover:bg-destructive/90 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                      >
                        <X className="h-2.5 w-2.5 text-destructive-foreground" />
                      </button>
                    )}
                    {selectedIconNode || <ChevronDown />}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0 z-[--swivu-bryntum-z-index] " align="start">
                  <Command>
                    <CommandInput placeholder={`Search ${label}...`} />
                    <CommandList>
                      <CommandEmpty>No icons found.</CommandEmpty>
                      <CommandGroup>
                        {options.map((option) => (
                          <CommandItem key={option.value} value={String(option.value)} onSelect={() => handleValueChange(option.value)}>
                            <Check className={cn("mr-2 h-4 w-4", selectedValue === option.value ? "opacity-100" : "opacity-0")} />
                            <div className="flex items-center gap-2">{option.icon}</div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </TooltipTrigger>
            <TooltipContent>
              <p>Icon color is inherited from Team</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
